import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  DateFnsAdapter,
  MAT_DATE_FNS_FORMATS,
} from '@angular/material-date-fns-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { ApiConfiguration } from '@core/api/angular';
import {
  AMPLIFY_OUTPUTS,
  APPConfig,
  AppVersion,
  CognitoKeyValueStorage,
  GAConfig,
} from '@lassi/config';
import { authInterceptor, ROUTE_STORAGE } from '@lassi/webapp-shared';
import { provideNgIdle } from '@ng-idle/core';
import { GoogleTagManagerConfiguration } from 'angular-google-tag-manager';
import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { enAU } from 'date-fns/locale';
import { firstValueFrom } from 'rxjs';
import { appRoutes } from './app.routes';
import { BROADCAST_MESSAGE_STORAGE } from './broadcast-message/broadcast-msg.service';
import { InactivityService } from './inactivity/inactivity.service';
import { BOTTOM_SHEET_STORAGE } from './map/menu/menu.guard';

function getAnimationProvider() {
  /* disable angular animation if reduced motion is set (in webapp e2e tests) */
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;
  return prefersReducedMotion
    ? provideNoopAnimations()
    : provideAnimationsAsync();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptors([authInterceptor])),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withComponentInputBinding()),
    getAnimationProvider(),
    provideNgIdle(),
    /*
     * Fix for manual date input format issue:
     * When users type dates in dd/mm/yyyy format manually, the Angular Material
     * Datepicker incorrectly parses them as mm/dd/yyyy format.
     *
     * This is a known issue with Angular Material Datepicker.
     * Reference: https://github.com/angular/components/issues/14291
     */
    { provide: DateAdapter, useClass: DateFnsAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: enAU },
    { provide: LOCALE_ID, useValue: 'en-AU' },
    { provide: ApiConfiguration, useValue: { rootUrl: '/api/core' } },
    provideAppInitializer(async () => {
      const amplifyConfig$ = inject(AMPLIFY_OUTPUTS);

      const outputs = await firstValueFrom(amplifyConfig$);

      Amplify.configure(outputs);
      cognitoUserPoolsTokenProvider.setKeyValueStorage(
        new CognitoKeyValueStorage(),
      );
    }),
    provideAppInitializer(async () => {
      const service = inject(InactivityService);
      const config = await firstValueFrom(inject(APPConfig));

      service.setup(config.inactivity.idleTime, config.inactivity.timeoutTime);
    }),
    provideAppInitializer(async () => {
      const gaConfig$ = inject(GAConfig);
      const tagManagerConfiguration = inject(GoogleTagManagerConfiguration);

      const config = await firstValueFrom(gaConfig$);
      if (config) {
        tagManagerConfiguration.set(config);
      }
    }),
    provideAppInitializer(async () => {
      await firstValueFrom(inject(AppVersion));
    }),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        // Prevent blinking
        showDelay: 200,
        tooltipClass: 'tooltip-text',
      },
    },
    {
      provide: BOTTOM_SHEET_STORAGE,
      useValue: window.sessionStorage,
    },
    {
      provide: ROUTE_STORAGE,
      useValue: window.sessionStorage,
    },
    {
      provide: BROADCAST_MESSAGE_STORAGE,
      useValue: window.localStorage,
    },
  ],
};
